/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, Grid, MenuItem } from '@material-ui/core';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { PartnerPartnership } from '../../../../models';
import { useAppData } from '../../../../providers';
import { CheckIcon, CustomDialogTitle, FieldLabel, InputField, MultiSelectControl } from '../../../../components';
import { useStyles } from './styles';
import { NewRepresentative, Representative } from '../../RepresentativesModels';
import { useCommonStyles, variables } from '../../../../styles';

interface Props {
  open: boolean;
  representative?: Representative;
  translation: any;
  partnerships: PartnerPartnership[];
  onSubmit: (values: any, { setSubmitting }: any) => void;
  onCancel: () => void;
}

export const InviteRepresentativeDialog: FC<Props> = ({ open, representative, translation, partnerships, onCancel, onSubmit }) => {
  const classes = useStyles();
  const commonClasses = useCommonStyles(true);
  const { userData } = useAppData();
  const [initialValues, setInitialValues] = useState<NewRepresentative>({
    email: '',
    assignedPartnershipCodes: [],
    displayName: '',
    partnerFID: '',
    password: '',
  });
  const [selectedPartnerships, setSelectedPartnerships] = useState<string[]>([]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (window.innerWidth <= 600) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }

    window.addEventListener('resize', () => {
      if (window.innerWidth <= 540) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    });
  }, []);

  useEffect(() => {
    const user = JSON.parse(userData);
    let initValues: NewRepresentative = {
      email: '',
      assignedPartnershipCodes: [],
      displayName: '',
      partnerFID: user.partnerID,
      password: '',
    };
    setSelectedPartnerships([translation.specify]);

    if (representative) {
      const partnershipCodes = representative.representativeAssignedPartnerships ? representative.representativeAssignedPartnerships.map((p) => (p.partnershipCode ? p.partnershipCode : '')) : [];
      setSelectedPartnerships(partnershipCodes);

      initValues = {
        id: representative.id,
        email: representative.email,
        assignedPartnershipCodes: partnershipCodes,
        displayName: representative.displayName,
        partnerFID: representative.partnerFID,
        password: '',
      };
    }
    setInitialValues(initValues);
  }, [representative, userData, translation]);

  const handleSelectChange = useCallback(
    async (event, setFieldValue) => {
      let options = event.target.value as string[];

      if (options.length === 0) {
        options = [translation.specify];
        setFieldValue('assignedPartnershipCodes', []);
      } else if (options.length === 1) {
        if (options[0] === translation.specify) {
          setFieldValue('assignedPartnershipCodes', []);
        } else if (options[0] === 'All') {
          const selected = partnerships.map((p) => p.partnershipCode ?? '');
          setFieldValue('assignedPartnershipCodes', selected);
        } else {
          setFieldValue('assignedPartnershipCodes', options);
        }
      } else {
        if (options.includes('All')) {
          if (options[options.length - 1] === 'All') {
            options = options.filter((option) => option === 'All');
            const selected: string[] = [];
            partnerships.forEach((partnership) => {
              selected.push(partnership.partnershipCode ? partnership.partnershipCode : '');
            });
            setFieldValue('assignedPartnershipCodes', selected);
          } else {
            options = options.filter((option) => option !== 'All' && option !== translation.specify);
            setFieldValue('assignedPartnershipCodes', options);
          }
        } else {
          setFieldValue('assignedPartnershipCodes', options);
        }

        if (options.includes(translation.specify)) {
          if (options[options.length - 1] === translation.specify) {
            options = options.filter((option) => option === translation.specify);
            setFieldValue('assignedPartnershipCodes', []);
          } else {
            options = options.filter((option) => option !== 'All' && option !== translation.specify);
            setFieldValue('assignedPartnershipCodes', options);
          }
        }
      }

      setSelectedPartnerships(options);
    },
    [partnerships, translation],
  );

  const validationSchema = Yup.object().shape({
    email: Yup.string().email(translation.email_must_be_a_valid_email).max(100, translation.text_6672).required(translation.text_586),
    assignedPartnershipCodes: Yup.mixed().test('arraySize', translation.text_6671, (value) => value.length > 0),
    displayName: Yup.string().max(20, translation.text_6707).required(translation.text_6674),
  });

  const handleClose = useCallback(
    (resetForm) => {
      onCancel();
      resetForm();
    },
    [onCancel],
  );

  const renderValueCustomCondition = useCallback(
    (value: string[]) => {
      if (value[0] === translation.specify) {
        return [translation.specify];
      }

      if (value[0] === 'All') {
        return [translation.all];
      }

      return false;
    },
    [translation.all],
  );

  return (
    <Formik enableReinitialize onSubmit={onSubmit} initialValues={initialValues} validationSchema={validationSchema}>
      {({ values, submitForm, resetForm, touched, errors, setFieldValue }) => (
        <Dialog onClose={() => handleClose(resetForm)} open={open} classes={{ paper: representative ? classes.dialog : classes.newDialog }}>
          <CustomDialogTitle title={representative ? translation.edit_representative : translation.text_6529} onClose={() => handleClose(resetForm)} />
          <Grid container direction="column">
            <Form>
              <Grid container direction="column" alignItems="flex-start" alignContent="flex-start" justify="space-between">
                <Grid container direction={isMobile ? 'column' : 'row'} justify="space-between" spacing={2} className={representative ? classes.hide : ''}>
                  <Grid item xs={isMobile ? 12 : 6}>
                    <InputField name="email" placeholder={translation.specify} label={translation.text_6530} isRequired errors={touched.email ? errors.email : undefined}>
                      {values.email}
                    </InputField>
                  </Grid>
                  <Grid item xs={isMobile ? 12 : 6}>
                    <InputField name="displayName" placeholder={translation.specify} label={translation.text_6531} isRequired errors={touched.displayName ? errors.displayName : undefined}>
                      {values.displayName}
                    </InputField>
                  </Grid>
                </Grid>
                <FieldLabel isRequired label={translation.text_115} />
                <MultiSelectControl
                  value={selectedPartnerships}
                  className={classes.multiSelect}
                  style={{ color: selectedPartnerships.includes(translation.specify) ? '#030C2133' : variables.richBlack }}
                  errors={touched.assignedPartnershipCodes ? errors.assignedPartnershipCodes : undefined}
                  onChange={(event: any) => handleSelectChange(event, setFieldValue)}
                  renderValueCustomCondition={renderValueCustomCondition}
                >
                  <MenuItem key="none" className={selectedPartnerships.includes(translation.specify) ? classes.placeholder : classes.option} value={translation.specify}>
                    <span>{translation.specify}</span>
                  </MenuItem>
                  <MenuItem key="all" className={selectedPartnerships.includes('All') ? classes.selectedOption : classes.option} value="All">
                    <CheckIcon />
                    <span>{translation.all}</span>
                  </MenuItem>
                  {partnerships.map((partnership) => (
                    <MenuItem
                      className={selectedPartnerships.includes(partnership.partnershipCode as string) ? classes.selectedOption : classes.option}
                      key={partnership.partnershipCode}
                      value={partnership.partnershipCode ? partnership.partnershipCode : ''}
                    >
                      <CheckIcon />
                      <span>{partnership.partnershipCode}</span>
                    </MenuItem>
                  ))}
                </MultiSelectControl>
              </Grid>
            </Form>
          </Grid>
          <DialogActions className={classes.dialogAction}>
            {representative ? (
              <Button disabled={values.assignedPartnershipCodes.length === 0} onClick={submitForm} startIcon={<CheckIcon />} className={`${commonClasses.activeDialogBtn} ${classes.btn}`}>
                {translation.text_10}
              </Button>
            ) : (
              <Button disabled={values.email === '' || values.displayName === '' || values.assignedPartnershipCodes.length === 0} onClick={submitForm} className={`${commonClasses.activeDialogBtn} ${classes.btn}`}>
                {translation.text_6529}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}
    </Formik>
  );
};
